import { Bell, Command, Import, Menu, Moon, Plus, Sun } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useNotifications } from '../lib/notifications.js';
import { useTheme } from '../lib/theme.js';

import { CommandPalette } from './CommandPalette.js';
import { NotificationPanel } from './NotificationPanel.js';

export function Topbar({ onMenuClick, navOpen }: { onMenuClick: () => void; navOpen: boolean }) {
  const { resolved, toggle } = useTheme();
  const navigate = useNavigate();
  // 别名管理页自带「导入 / 新建别名」，这里再放一组就重复了。
  const onAliasesPage = useLocation().pathname.startsWith('/app/aliases');

  const notif = useNotifications();
  const unread = notif.data?.unread ?? 0;
  const [notifOpen, setNotifOpen] = useState(false);

  // F60：⌘K / Ctrl+K → 打开 CommandPalette
  // preventDefault 抢占浏览器/系统级 ⌘K（Chrome/Edge 默认地址栏聚焦；Firefox 会走 Quick Find），
  // 只在没有输入元素聚焦时抢，避免打断用户在别的表单里输入 ⌘K 触发的场景。
  const [paletteOpen, setPaletteOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        e.stopPropagation();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <header className="flex h-16 shrink-0 items-center gap-2 border-b border-slate-200 bg-white px-3 dark:border-slate-800 dark:bg-slate-950 md:gap-4 md:px-6">
      {/* 移动端汉堡：md 以上侧栏常驻，不需要。 */}
      <button
        type="button"
        onClick={onMenuClick}
        aria-label="打开菜单"
        aria-expanded={navOpen}
        className="touch-target -ml-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-900 md:hidden"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* min-w-0：flex item 默认 min-width:auto，不加这个搜索框无法收缩、会把右侧按钮挤出视口。 */}
      <div className="relative min-w-0 flex-1 md:max-w-xl">
        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          className="flex h-9 w-full items-center rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-3 text-left text-sm outline-none hover:bg-white focus:border-brand-400 focus:bg-white dark:border-slate-800 dark:bg-slate-900 dark:hover:bg-slate-950 sm:pr-16"
          aria-label="打开全局搜索（⌘K）"
        >
          <span className="truncate text-slate-400">
            {/* 窄屏放不下长提示语，缩成「搜索」二字，避免被 truncate 砍成无意义的半句。 */}
            <span className="sm:hidden">搜索</span>
            <span className="hidden whitespace-nowrap sm:inline">搜索别名或邮件…</span>
          </span>
        </button>
        <Command className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        {/* ⌘K 徽标在触屏上没有意义，窄屏直接隐藏把空间还给文案。 */}
        <span className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 items-center gap-1 text-xs text-slate-400 sm:flex">
          <kbd className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-medium dark:border-slate-700 dark:bg-slate-900">
            ⌘K
          </kbd>
        </span>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />

      {/* ml-auto：搜索框限宽后，操作区贴右对齐页面内容右缘，别跟在搜索框后面留一大片空白。 */}
      <div className="relative ml-auto flex shrink-0 items-center gap-1 md:gap-2">
        <IconBtn label={resolved === 'dark' ? '切换到浅色' : '切换到深色'} onClick={toggle}>
          {resolved === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </IconBtn>
        <IconBtn
          label="通知"
          {...(unread > 0 ? { badge: String(unread > 99 ? '99+' : unread) } : {})}
          onClick={() => setNotifOpen((v) => !v)}
          active={notifOpen}
        >
          <Bell className="h-4 w-4" />
        </IconBtn>
        {notifOpen ? <NotificationPanel onClose={() => setNotifOpen(false)} /> : null}
        {/* 系统设置只走侧栏入口，顶栏不再放重复的齿轮图标。 */}
        {onAliasesPage ? null : (
          <>
            <ActionBtn
              label="导入别名"
              onClick={() => navigate('/app/aliases?import=1')}
              className="ml-1 border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
            >
              <Import className="h-4 w-4" />
            </ActionBtn>
            <ActionBtn
              label="创建别名"
              onClick={() => navigate('/app/aliases?create=1')}
              className="bg-brand-500 text-white hover:bg-brand-600"
            >
              <Plus className="h-4 w-4" />
            </ActionBtn>
          </>
        )}
      </div>
    </header>
  );
}

/** 带文案的主操作按钮：窄屏只留图标（文案放 aria-label），lg 以上显示完整中文文案。 */
function ActionBtn({
  label,
  onClick,
  className,
  children,
}: {
  label: string;
  onClick: () => void;
  className: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={
        'touch-target inline-flex h-9 w-9 items-center justify-center gap-1.5 rounded-lg text-sm font-medium lg:w-auto lg:px-3 ' +
        className
      }
    >
      {children}
      <span className="hidden whitespace-nowrap lg:inline">{label}</span>
    </button>
  );
}

function IconBtn({
  label,
  onClick,
  badge,
  active,
  children,
}: {
  label: string;
  onClick?: () => void;
  badge?: string;
  active?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-expanded={active ?? false}
      onClick={onClick}
      className={
        // touch-target：视觉仍是 36px，触屏上点击热区补到 44px（见 index.css）。
        'touch-target relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors ' +
        (active
          ? 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100'
          : 'hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-900 dark:hover:text-slate-100')
      }
    >
      {children}
      {badge ? (
        <span className="absolute -right-0.5 -top-0.5 min-w-[16px] rounded-full bg-red-500 px-1 text-[10px] font-medium leading-4 text-white">
          {badge}
        </span>
      ) : null}
    </button>
  );
}
